import { useTheme } from "@emotion/react";
import useStyles from "./styles";
import { Box, Stack } from "@mui/system";
import {
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";

import { useOutletContext } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import RamGraphComponent from "../../../../components/saude/ramGraphComponent";
import MultiMetricGraphComponent from "../../../../components/saude/multiMetricGraphComponent";
import dayjs from "dayjs";
import DatagridComponent from "../../../../components/datagridComponent";
import CardStatus from "../../../../components/saude/cardStatusHealth";
import { DeviceThermostatOutlined, FlashOnOutlined } from "@mui/icons-material";
import CardGraphComponent from "../../../../components/cardGraphComponent";
import SeverityChip from "../../../../components/severityChip";

export default function DashHealth() {
  const {
    filterData,
    handleAplicarZoom,
    setCounter,
    setLoadingCronometro,
    selectTime,
    optionsTime,
    startCronometro,
    controllerDashboard,
    setControllerDashboard,
    api,
    stopCronometro,
    deviceConfig,
  } = useOutletContext();
  const theme = useTheme();
  const styles = useStyles(theme);
  const [dataCpu, setDataCpu] = useState([]);
  const [dataRamUsage, setDataRamUsage] = useState([]);
  const [dataCpuNow, setDataCpuNow] = useState([]);
  const [dataRamUsageNow, setDataRamUsageNow] = useState([]);
  const [modelSystem, setModelSystem] = useState("");
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const [sysUpTime, setSysUpTime] = useState("");
  const [systemName, setSystemName] = useState("");
  const [loadingSystemName, setLoadingSystemName] = useState(false);
  const [loadingSysUpTime, setLoadingSysUpTime] = useState(false);
  const [loadingModelSystem, setLoadingModelSystem] = useState(false);
  const [loadingFirmwareVersion, setLoadingFirmwareVersion] = useState(false);
  const [loadingDashHealth, setLoadingDashHealth] = useState(false);
  const [loadingRamUsage, setLoadingRamUsage] = useState(false);
  const [temperatura, setTemperatura] = useState([]);
  const [loadingTemperatura, setLoadingTemperatura] = useState(false);
  const [voltagem, setVoltagem] = useState([]);
  const [loadingVoltagem, setLoadingVoltagem] = useState(false);
  const [temperaturaNow, setTemperaturaNow] = useState("0 Cº");
  const [alerts, setAlerts] = useState([]);
  const [voltagemNow, setVoltagemNow] = useState("0 V");
  const [voltagemRawNow, setVoltagemRawNow] = useState(null);
  const [temperaturaRawNow, setTemperaturaRawNow] = useState(null);

  const clearData = () => {
    setDataCpu([]);
    setDataCpuNow([]);
    setDataRamUsage([]);
    setDataRamUsageNow([]);
    setModelSystem("");
    setFirmwareVersion("");
    setSysUpTime("");
    setSystemName("");
    setTemperatura([]);
    setVoltagem([]);
    setTemperaturaNow("0 Cº");
    setAlerts([]);
    setVoltagemNow("0 V");
    setVoltagemRawNow(null);
    setTemperaturaRawNow(null);
  };

  // Controller do fetch desta página. O slot compartilhado (controllerDashboard)
  // é disputado por getClients/getServicesCliente/getInterfaceOptions do layout,
  // então quem sobrescreve o slot nos faz perder a referência do nosso próprio
  // fetch. O ref garante que a página sempre consiga cancelar a onda anterior.
  const fetchControllerRef = useRef(null);

  useEffect(() => {
    if (
      filterData?.interfaceId &&
      filterData?.hostId &&
      filterData?.initialDate &&
      filterData?.finalDate
    ) {
      fetchControllerRef.current?.abort();
      const newController = new AbortController();
      fetchControllerRef.current = newController;
      setControllerDashboard(newController);
      fetchAllData(newController.signal);
    } else {
      clearData();
    }
    // Datas viram primitivo: o layout as escreve como Date e, na restauração do
    // localStorage, como string — sem normalizar, valores iguais não colapsam.
  }, [
    filterData?.hostId,
    filterData?.interfaceId,
    filterData?.device,
    filterData?.initialDate ? +new Date(filterData.initialDate) : null,
    filterData?.finalDate ? +new Date(filterData.finalDate) : null,
  ]);
  useEffect(() => {
    return () => {
      // Antes lia controllerDashboard da closure do 1º render (sempre null) e
      // portanto nunca abortava nada ao desmontar.
      fetchControllerRef.current?.abort();
      stopCronometro();
    };
  }, []);

  const fetchAllData = async (signal) => {
    setCounter(null);
    stopCronometro();
    setLoadingCronometro(true);

    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;

    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime[selectTime]?.value;
      finalDate = dayjs().set("second", 0).set("millisecond", 0).toDate();
    }
    await Promise.all([
      getRamUsage(dateInitial, finalDate, signal),
      getDataHealth(dateInitial, finalDate, signal),
      getSystemModel(dateInitial, finalDate, signal),
      getSysUpTime(dateInitial, finalDate, signal),
      getSystemName(dateInitial, finalDate, signal),
      getTemperatura(dateInitial, finalDate, signal),
      getVoltagem(dateInitial, finalDate, signal),
      getVoltagemNow(dateInitial, finalDate, signal),
      getTemperaturaNow(dateInitial, finalDate, signal),
      getAlerts(dateInitial, finalDate, signal),
    ]);
    if (!signal.aborted) {
      setLoadingCronometro(false);
      startCronometro();
    }
  };

  const getRamUsage = async (dateInitial, finalDate, signal) => {
    setLoadingRamUsage(true);
    try {
      const [ramHistory, ramNow] = await Promise.all([
        api.post(
          "/zabbix/serieTemporal",
          {
            device: filterData?.device,
            initial_date: dateInitial,
            final_date: finalDate,
            hostids: filterData?.hostId,
            nameFilter: "ramUsage",
            fabricante: filterData?.fabricante,
            funcao: filterData?.funcao,
            modelo: filterData?.modelo,
            dash: "health",
          },
          { signal },
        ),
        api.post(
          "/zabbix/serieTemporal",
          {
            device: filterData?.device,
            initial_date: dateInitial,
            final_date: finalDate,
            hostids: filterData?.hostId,
            nameFilter: "ramUsage",
            lastItem: true,
            lastDay: true,
            fabricante: filterData?.fabricante,
            funcao: filterData?.funcao,
            modelo: filterData?.modelo,
            dash: "health",
          },
          { signal },
        ),
      ]);

      setDataRamUsage(ramHistory?.data);
      setDataRamUsageNow(ramNow?.data);
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingRamUsage(false);
      }
    }
  };

  const getDataHealth = async (dateInitial, finalDate, signal) => {
    setLoadingDashHealth(true);
    try {
      const [historyResponse, nowResponse] = await Promise.all([
        api.post(
          "/zabbix/serieTemporal",
          {
            initial_date: dateInitial,
            final_date: finalDate,
            device: filterData?.device,
            hostids: filterData?.hostId,
            nameFilter: "cpuUsage",
            fabricante: filterData?.fabricante,
            funcao: filterData?.funcao,
            modelo: filterData?.modelo,
            dash: "health",
          },
          { signal },
        ),
        api.post(
          "/zabbix/serieTemporal",
          {
            initial_date: dateInitial,
            final_date: finalDate,
            device: filterData?.device,
            hostids: filterData?.hostId,
            nameFilter: "cpuUsage",
            lastItem: true,
            lastDay: true,
            fabricante: filterData?.fabricante,
            funcao: filterData?.funcao,
            modelo: filterData?.modelo,
            dash: "health",
          },
          { signal },
        ),
      ]);

      setDataCpu(historyResponse?.data ?? {});
      setDataCpuNow(nowResponse?.data ?? {});
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingDashHealth(false);
      }
    }
  };

  const getSystemModel = async (dateInitial, finalDate, signal) => {
    setLoadingModelSystem(true);
    try {
      const firmwareVersionResult = await getFirmwareVersion(
        dateInitial,
        finalDate,
        signal,
      );

      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          hostids: filterData?.hostId,
          nameFilter: "systemModel",
          exactMatch: true,
          lastItem: true,
          lastDay: true,
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );

      if (!firmwareVersionResult) {
        const val = response?.data?.value || "";
        if (val.toLowerCase().includes("huawei")) {
          setModelSystem(val.split(" ")[0] || "");
          const fnMatch = val.match(/version\s+([^\s(]+)/i);
          setFirmwareVersion(fnMatch?.[1] || "");
        } else {
          setModelSystem(val.split("(")?.[0] || "");
          // Faz regex para obter toda a string entre  ( )
          const firmwareVersion = val.match(/\((.*?)\)/)?.[1];
          // Remove "version"
          const withoutVersion = firmwareVersion?.replace(" version", "");
          setFirmwareVersion(withoutVersion || "");
        }
      } else {
        setModelSystem(response?.data?.value || "");
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingModelSystem(false);
      }
    }
  };

  const getFirmwareVersion = async (dateInitial, finalDate, signal) => {
    setLoadingFirmwareVersion(true);
    try {
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          hostids: filterData?.hostId,
          nameFilter: "firmwareVersion",
          exactMatch: true,
          lastItem: true,
          lastDay: true,
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );
      const value = response?.data?.value || "";
      setFirmwareVersion(value);
      return value;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      if (!signal.aborted) {
        setLoadingFirmwareVersion(false);
      }
    }
  };
  const getSysUpTime = async (dateInitial, finalDate, signal) => {
    setLoadingSysUpTime(true);
    try {
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          hostids: filterData?.hostId,
          nameFilter: "sysUpTime",
          lastItem: true,
          lastDay: true,
          exactMatch: true,
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );
      setSysUpTime(formatSysUpTime(response?.data?.value));
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingSysUpTime(false);
      }
    }
  };

  const getSystemName = async (dateInitial, finalDate, signal) => {
    setLoadingSystemName(true);
    try {
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          hostids: filterData?.hostId,
          nameFilter: "systemName",
          lastItem: true,
          lastDay: true,
          exactMatch: true,
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );
      setSystemName(response?.data.value || "");
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingSystemName(false);
      }
    }
  };
  const getTemperatura = async (dateInitial, finalDate, signal) => {
    setLoadingTemperatura(true);
    try {
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          initial_date: dateInitial,
          final_date: finalDate,
          interface: filterData?.interfaceId,
          hostids: filterData?.hostId,
          nameFilter: "equipmentTemperature",
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );
      setTemperatura(response?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingTemperatura(false);
      }
    }
  };
  const getVoltagem = async (dateInitial, finalDate, signal) => {
    setLoadingVoltagem(true);
    try {
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          interface: filterData?.interfaceId,
          initial_date: dateInitial,
          final_date: finalDate,
          hostids: filterData?.hostId,
          nameFilter: "supplyVoltage",
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );
      setVoltagem(response?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingVoltagem(false);
      }
    }
  };
  const getVoltagemNow = async (dateInitial, finalDate, signal) => {
    try {
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          interface: filterData?.interfaceId,
          hostids: filterData?.hostId,
          lastItem: true,
          lastDay: true,
          nameFilter: "supplyVoltage",
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );
      setVoltagemRawNow(response?.data);
      let val = 0;
      if (response?.data?.value !== undefined) {
        val = response?.data?.value;
      } else if (Array.isArray(response?.data) && response?.data.length > 0) {
        val = response?.data[0]?.value;
      } else if (response?.data && typeof response?.data === "object") {
        const keys = Object.keys(response?.data);
        if (keys.length > 0) val = response?.data[keys[0]]?.value;
      }
      setVoltagemNow(formattedVoltagem(val));
    } catch (error) {
      console.error(error);
    } finally {
    }
  };
  const getTemperaturaNow = async (dateInitial, finalDate, signal) => {
    try {
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          device: filterData?.device,
          interface: filterData?.interfaceId,
          hostids: filterData?.hostId,
          lastItem: true,
          lastDay: true,
          nameFilter: "equipmentTemperature",
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "health",
        },
        { signal },
      );
      setTemperaturaRawNow(response?.data);
      let val = 0;
      if (response?.data?.value !== undefined) {
        val = response?.data?.value;
      } else if (Array.isArray(response?.data) && response?.data.length > 0) {
        val = response?.data[0]?.value;
      } else if (response?.data && typeof response?.data === "object") {
        const keys = Object.keys(response?.data);
        if (keys.length > 0) val = response?.data[keys[0]]?.value;
      }
      setTemperaturaNow(formattedTemperature(val));
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const getAlerts = async (dateInitial, finalDate, signal) => {
    try {
      const response = await api.post(
        "/zabbix/items/alerts",
        {
          initial_date: dateInitial,
          final_date: finalDate,
          interface: filterData?.interfaceId,
          hostids: filterData?.hostId,
          equipment: filterData?.device,
          alertType: "health",
          // Restringe os alertas às interfaces do cliente quando o host está
          // no provedor (tag locatedAt = PROVEDOR)
          clienteId: filterData?.clienteId,
          servicoId: filterData?.serviceId,
        },
        { signal },
      );

      setAlerts(
        response?.data?.map((item, index) => {
          return {
            ...item,
            id: index,
          };
        }) || [],
      );
    } catch (error) {
      console.error(error);
    } finally {
    }
  };
  const columns = [
    {
      field: "alert",
      headerName: "Alerta",
      flex: 2,
      valueFormatter: (params) => params?.replaceAll('"', ""),
    },
    {
      field: "severity",
      headerName: "Severidade",
      flex: 1,
      minWidth: 115,
      renderCell: (params) => (
        <SeverityChip severity={params?.row?.severity} theme={theme} />
      ),
    },
    {
      field: "initialDate",
      headerName: "Horário Ocorrência",
      flex: 1.5,
      valueFormatter: (params) =>
        dayjs.unix(Number(params)).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      field: "recoveryDate",
      headerName: "Horário Recuperação",
      flex: 1.5,
      valueFormatter: (params) =>
        dayjs.unix(Number(params)).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      field: "duration",
      headerName: "Duração",
      flex: 1,
      valueFormatter: (params) => {
        const totalMs = Number(params);
        if (!params || isNaN(totalMs) || totalMs <= 0) {
          return "";
        }
        const yearMs = 31536000000;
        const monthMs = 2592000000;
        const dayMs = 86400000;
        const hourMs = 3600000;
        const minuteMs = 60000;
        const secondMs = 1000;

        let remaining = totalMs;
        const years = Math.floor(remaining / yearMs);
        remaining %= yearMs;

        const months = Math.floor(remaining / monthMs);
        remaining %= monthMs;

        const days = Math.floor(remaining / dayMs);
        remaining %= dayMs;

        const hours = Math.floor(remaining / hourMs);
        remaining %= hourMs;

        const minutes = Math.floor(remaining / minuteMs);
        remaining %= minuteMs;

        const seconds = Math.floor(remaining / secondMs);

        const parts = [];
        if (years > 0) parts.push(`${years}a`);
        if (months > 0) parts.push(`${months}mes`);
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0) parts.push(`${seconds}s`);

        return parts.join(" ");
      },
    },
  ];

  function formatSysUpTime(value) {
    if (value === undefined || value === null || value === "") {
      return "";
    }

    const totalSeconds = Math.floor(Number(value));

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }
  const formattedTemperature = (value) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return "0 Cº";
    return `${numericValue.toFixed(1)} Cº`;
  };
  const formattedVoltagem = (value) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return "0 v";
    return `${numericValue.toFixed(1)} V`;
  };

  const CustomTooltipTemperatura = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const { clock, value } = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: theme.palette.paletteCharts.lineCharts,
          padding: "8px",
          borderRadius: "8px",
        }}
      >
        <Typography fontSize={"14px"}>
          {`Data: ${dayjs.unix(clock).format("DD/MM/YY HH:mm")}`}
        </Typography>

        <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
          <Box
            sx={{
              bgcolor: theme.palette.cardGraphic.graphColor["green"],
              width: "14px",
              height: "14px",
              borderRadius: "2px",
              border: `1px solid ${theme.palette.cardGraphic.graphColor["green"]}`,
            }}
          />
          <Typography fontSize={"14px"}>
            Temperatura: {formattedTemperature(value)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  const CustomTooltipVoltagem = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const { clock, value } = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: theme.palette.paletteCharts.lineCharts,
          padding: "8px",
          borderRadius: "8px",
        }}
      >
        <Typography fontSize={"14px"}>
          {`Data: ${dayjs.unix(clock).format("DD/MM/YY HH:mm")}`}
        </Typography>

        <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
          <Box
            sx={{
              bgcolor: theme.palette.cardGraphic.graphColor["green"],
              width: "14px",
              height: "14px",
              borderRadius: "2px",
              border: `1px solid ${theme.palette.cardGraphic.graphColor["green"]}`,
            }}
          />
          <Typography fontSize={"14px"}>
            Tensão: {formattedVoltagem(value)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  const shouldUseMultiMetricForSensors =
    (voltagem &&
      !Array.isArray(voltagem) &&
      typeof voltagem === "object" &&
      Object.keys(voltagem).length > 0) ||
    (temperatura &&
      !Array.isArray(temperatura) &&
      typeof temperatura === "object" &&
      Object.keys(temperatura).length > 0);

  const shouldHideSensors =
    filterData?.fabricante?.toLowerCase?.().includes("fortinet") ||
    filterData?.modelo?.toLowerCase?.().includes("fortigate") ||
    modelSystem?.toLowerCase?.().includes("fortigate");

  return (
    <Box sx={styles.container}>
      <Grid container columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}>
        <Grid item xs={2} sm={4} md={6} lg={6}>
          <Grid
            container
            columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
            flexGrow={1}
            spacing={1}
            alignItems="stretch"
          >
            <Grid item xs={2} sm={4} md={6} lg={6} xl={6} xxl={6}>
              <Grid
                container
                columns={{ xs: 2, sm: 4, md: 4, lg: 4, xl: 4, xxl: 4 }}
                flexGrow={1}
                spacing={1}
                alignItems="stretch"
              >
                <Grid item xs={2} sm={2} md={2} lg={2} xl={1} xxl={1}>
                  <CardStatus
                    titleCard={"Tempo online"}
                    valueCard={sysUpTime || "Desconhecido"}
                    loading={loadingSysUpTime}
                  />
                </Grid>
                <Grid item xs={2} sm={2} md={2} lg={2} xl={1} xxl={1}>
                  <CardStatus
                    typeCard={2}
                    titleCard={"Sistema"}
                    valueCard={modelSystem || "Desconhecido"}
                    loading={loadingModelSystem}
                  />
                </Grid>
                <Grid item xs={2} sm={2} md={2} lg={2} xl={1} xxl={1}>
                  <CardStatus
                    typeCard={3}
                    titleCard={"Firmware (versão)"}
                    valueCard={firmwareVersion || "Desconhecido"}
                    loading={loadingFirmwareVersion}
                  />
                </Grid>
                <Grid item xs={2} sm={2} md={2} lg={2} xl={1} xxl={1}>
                  <CardStatus
                    typeCard={4}
                    titleCard={"Dispositivo"}
                    valueCard={systemName || "Desconhecido"}
                    loading={loadingSystemName}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2} sm={4} md={6} lg={6} xl={3} display="flex">
              <MultiMetricGraphComponent
                data={dataCpu}
                dataNow={dataCpuNow}
                titleGraph={"CPU"}
                unit={"%"}
                loading={loadingDashHealth}
                handleAplicarZoom={handleAplicarZoom}
                isZoomEnabled={true}
              />
            </Grid>

            <Grid item xs={2} sm={4} md={6} lg={6} xl={3} display="flex">
              {dataRamUsage &&
                !Array.isArray(dataRamUsage) &&
                typeof dataRamUsage === "object" &&
                Object.keys(dataRamUsage).length > 0 ? (
                <MultiMetricGraphComponent
                  data={dataRamUsage}
                  dataNow={dataRamUsageNow}
                  titleGraph={"RAM"}
                  unit={"%"}
                  loading={loadingRamUsage}
                  handleAplicarZoom={handleAplicarZoom}
                  isZoomEnabled={true}
                />
              ) : (
                <RamGraphComponent
                  data={Array.isArray(dataRamUsage) ? dataRamUsage : []}
                  titleGraph={"RAM"}
                  colorType={3}
                  isLatencia={true}
                  loading={loadingRamUsage}
                  valueRamNow={dataRamUsageNow?.value || 0}
                  handleAplicarZoom={handleAplicarZoom}
                  isZoomEnabled={true}
                />
              )}
            </Grid>
            {!shouldHideSensors && (
              <Grid item xs={2} sm={4} md={6} lg={6} xl={3} display="flex">
                {shouldUseMultiMetricForSensors ? (
                  <MultiMetricGraphComponent
                    data={voltagem}
                    dataNow={voltagemRawNow}
                    titleGraph={"Nível de tensão"}
                    unit={" V"}
                    loading={loadingVoltagem}
                    handleAplicarZoom={handleAplicarZoom}
                    isZoomEnabled={true}
                  />
                ) : (
                  <CardGraphComponent
                    loading={loadingVoltagem}
                    titleGraph={"Nível de tensão"}
                    data={Array.isArray(voltagem) ? voltagem : []}
                    icon={FlashOnOutlined}
                    valueCard={voltagemNow}
                    CustomTooltip={CustomTooltipVoltagem}
                    tickFormatter={formattedVoltagem}
                    iconColorCard={(() => {
                      const match =
                        typeof voltagemNow === "string"
                          ? voltagemNow.match(/\d+/)
                          : null;
                      const valor = match ? parseInt(match[0], 10) : null;

                      return valor !== null &&
                        !isNaN(valor) &&
                        (valor > 10.5 || valor <= 29.5)
                        ? "green"
                        : "orange";
                    })()}
                    handleAplicarZoom={handleAplicarZoom}
                    isZoomEnabled={true}
                  />
                )}
              </Grid>
            )}
            {!shouldHideSensors && (
              <Grid item xs={2} sm={4} md={6} lg={6} xl={3} display="flex">
                {shouldUseMultiMetricForSensors ? (
                  <MultiMetricGraphComponent
                    data={temperatura}
                    dataNow={temperaturaRawNow}
                    titleGraph={"Nível de temperatura"}
                    unit={" Cº"}
                    loading={loadingTemperatura}
                    handleAplicarZoom={handleAplicarZoom}
                    isZoomEnabled={true}
                  />
                ) : (
                  <CardGraphComponent
                    iconColorCard={(() => {
                      const match =
                        typeof temperaturaNow === "string"
                          ? temperaturaNow.match(/\d+/)
                          : null;
                      const valor = match ? parseInt(match[0], 10) : null;

                      if (valor !== null && !isNaN(valor)) {
                        if (valor < 40) return "green";
                        if (valor >= 40 && valor < 65) return "orange";
                        return "red";
                      }

                      return "gray"; // fallback se temperatura inválida
                    })()}
                    loading={loadingTemperatura}
                    titleGraph={"Nível de temperatura"}
                    data={Array.isArray(temperatura) ? temperatura : []}
                    icon={DeviceThermostatOutlined}
                    valueCard={temperaturaNow}
                    CustomTooltip={CustomTooltipTemperatura}
                    tickFormatter={formattedTemperature}
                    handleAplicarZoom={handleAplicarZoom}
                    isZoomEnabled={true}
                  />
                )}
              </Grid>
            )}
            <Grid item xs={2} sm={4} md={6} lg={6} xl={6}>
              <Paper
                sx={{
                  ...styles.paperDatagrid,
                  ...(alerts?.length > 11 && { height: "395px" }),
                }}
                elevation={theme.palette.mode == "dark" ? 0 : 3}
              >
                {loadingDashHealth && (
                  <LinearProgress sx={styles.linearProgress} />
                )}
                <Typography variant="h6" sx={styles.titleDatagrid}>
                  Alertas - CPU e RAM
                </Typography>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    ...(alerts?.length > 11 && {
                      height: "341px",
                    }),
                  }}
                >
                  <DatagridComponent columns={columns} rows={alerts} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
